import {
  Avatar,
  Button,
  Card,
  Col,
  Empty,
  Image,
  Modal,
  Row,
  Spin,
  Tag,
  message,
} from "antd";
import axios from "axios";
import moment from "moment";
import React, { useState } from "react";
import {
  MdOutlineBedtime,
  MdOutlineRestaurant,
  MdOutlineWc,
} from "react-icons/md";
import { useQuery, useQueryClient } from "react-query";
import DatePicker from "../../../../../components/inputs/MomentDatePicker";
import { cloudFrontUrl, serverUrl } from "../../../../../nestserver";
import AddActivityModal from "./AddActivityModal";

export const TYPE_META = {
  meal: { color: "orange", label: "Meal", icon: MdOutlineRestaurant },
  loo: { color: "blue", label: "Loo", icon: MdOutlineWc },
  sleep: { color: "purple", label: "Sleep", icon: MdOutlineBedtime },
};

const authHeaders = () => ({
  Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
});

const entrySummary = (entry) => {
  const time = entry.occurred_at ? moment(entry.occurred_at).format("LT") : "";
  if (entry.type === "meal") {
    const parts = [
      entry.meal_slot,
      entry.food,
      entry.amount && `Ate: ${entry.amount}`,
    ].filter(Boolean);
    return `${time}${parts.length ? " — " + parts.join(", ") : ""}`;
  }
  if (entry.type === "loo") {
    return `${time}${entry.loo_kind ? " — " + entry.loo_kind : ""}`;
  }
  if (entry.type === "sleep") {
    return entry.sleep_end && entry.duration_min
      ? `${time} — ${entry.duration_min} min nap`
      : `${time} — Nap started`;
  }
  return time;
};

const initials = (row) =>
  `${row?.student?.first_name?.[0] || ""}${row?.student?.last_name?.[0] || ""}`.toUpperCase();

const DailyActivityHome = () => {
  const queryClient = useQueryClient();
  const [selectedDate, setSelectedDate] = useState(moment());
  const [modalStudentId, setModalStudentId] = useState(null);
  const selectedDateStr = selectedDate.format("YYYY-MM-DD");

  const queryKey = ["daily-activity-roster", selectedDateStr];
  async function fetchDailyActivity() {
    const res = await axios.get(
      `${serverUrl}/montessori/daily-activity/roster?date=${selectedDateStr}`,
      { headers: authHeaders() },
    );
    return res.data?.result?.data || [];
  }
  const roster = useQuery(queryKey, fetchDailyActivity, {
    enabled: !!selectedDateStr,
  });

  const handleDeleteEntry = async (entryId) => {
    try {
      await axios.delete(
        `${serverUrl}/montessori/daily-activity/entry/${entryId}`,
        {
          headers: authHeaders(),
        },
      );
      queryClient.invalidateQueries(queryKey);
    } catch (err) {
      message.error(
        err?.response?.data?.error?.errors?.[0] ||
          err?.response?.data?.message ||
          "Could not delete that entry.",
      );
    }
  };

  const confirmDeleteEntry = (entryId) => {
    Modal.confirm({
      title: "Delete this entry?",
      content: "This action cannot be undone.",
      okText: "Delete",
      okButtonProps: { danger: true },
      cancelText: "Cancel",
      onOk: () => handleDeleteEntry(entryId),
    });
  };

  return (
    <div className="daily-activity pt-8">
      <div className="bg-white rounded-2xl border border-gray-100 p-4 sm:p-6">
        <div className="text-2xl text-orange-500 font-semibold mb-5">Day Care Activity</div>
        {/* Filter toolbar */}
        <Row
          gutter={[24, 16]}
          align="middle"
          className="pb-6 mb-6 border-b border-gray-100"
        >
          <Col xs={24} md={12} lg={8}>
            <div className="text-xs font-semibold text-orange-500 uppercase tracking-wide mb-1.5">
              Date
            </div>
            <DatePicker
              value={selectedDate}
              format="DD MMM YYYY"
              allowClear={false}
              className="w-full"
              disabledDate={(d) => !d || d.isAfter(moment().endOf("day"))}
              onChange={(date) => date && setSelectedDate(date)}
              getPopupContainer={(trigger) => trigger.parentNode}
            />
          </Col>
        </Row>

        {/* Content */}
        {roster.isLoading && (
          <div className="py-16 flex justify-center">
            <Spin />
          </div>
        )}

        {roster.isSuccess && roster.data.length === 0 && (
          <div className="py-16">
            <Empty description="No day-care-enabled students found" />
          </div>
        )}

        {roster.isSuccess && roster.data.length > 0 && (
          <Row gutter={[20, 20]}>
            {roster.data.map((row) => (
              <Col xs={24} lg={12} key={row.student?._id}>
                <Card
                  bordered
                  className="rounded-2xl h-full"
                  bodyStyle={{ padding: 20 }}
                >
                  <div className="flex items-center justify-between gap-3 mb-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <Avatar
                        style={{ backgroundColor: "#fdba74", flexShrink: 0 }}
                        icon={
                          <span style={{ fontSize: 14, fontWeight: 600 }}>
                            {initials(row)}
                          </span>
                        }
                      />
                      <div className="font-semibold text-base truncate">
                        {row.student?.first_name} {row.student?.last_name}
                      </div>
                    </div>
                    <Button
                      size="small"
                      type="primary"
                      ghost
                      onClick={() => setModalStudentId(row.student?._id)}
                    >
                      + Add
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {row.entries.length === 0 && (
                      <span className="text-gray-400 text-sm">
                        No entries logged yet
                      </span>
                    )}
                    {row.entries.map((entry) => {
                      const Icon = TYPE_META[entry.type]?.icon;
                      return (
                        <span key={entry._id} className="inline-flex items-center gap-1">
                          <Tag
                            color={TYPE_META[entry.type]?.color}
                            closable
                            onClose={(e) => {
                              e.preventDefault();
                              confirmDeleteEntry(entry._id);
                            }}
                            className="inline-flex items-center gap-1"
                            style={{ padding: "3px 8px" }}
                          >
                            {Icon && <Icon size={14} />}
                            {TYPE_META[entry.type]?.label}: {entrySummary(entry)}
                          </Tag>
                          {entry.photo && (
                            <Image
                              src={`${cloudFrontUrl}/${row.student?._id}/${entry.photo}`}
                              width={24}
                              height={24}
                              style={{ objectFit: "cover", borderRadius: 4 }}
                              preview={{ mask: false }}
                            />
                          )}
                        </span>
                      );
                    })}
                  </div>
                </Card>
              </Col>
            ))}
          </Row>
        )}
      </div>

      <AddActivityModal
        open={!!modalStudentId}
        onClose={() => setModalStudentId(null)}
        studentId={modalStudentId}
        date={selectedDate}
        onSaved={() => {
          setModalStudentId(null);
          queryClient.invalidateQueries(queryKey);
        }}
      />
    </div>
  );
};

export default DailyActivityHome;
